/**
 * Dependencies.
 */
import { Document, ExternalDocument } from 'pdfjs';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

import DocumentModel from '@/api/document/document.model';
import { documentService } from '@/api/document/document.service';
import { TimelineStepType } from '@/ui/components/timeline/timeline.component';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Component.
 */
@Component({
  components: {
    Timeline
  },
  name: 'select-type-scene'
})
export default class extends Vue {
  /**
   * Getter timeline.
   */
  get timeline() {
    return [TimelineStepType.Completed, TimelineStepType.Current];
  }

  /**
   * Handle go back.
   */
  handleGoBack() {
    this.$router.push({ name: 'ScanCodeDocument' });
  }

  /**
   * Handle document type selected.
   */
  async handleDocumentTypeSelected(type: 'DELIVERY' | 'DELIVERY_PACKAGE') {
    try {
      this.isLoading = true;

      const documents = await documentService.getByType(
        this.$route.params.code,
        type
      );

      await this.handleOpenDocument(documents);
    } catch (error) {
      this.$kit.notify.error(
        this.i18n('DOCUMENT.LABELS.ERROR'),
        this.i18n('DOCUMENT.ERRORS.GET_DOCUMENT')
      );

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleDocumentTypeSelected' });
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle open document.
   */
  async handleOpenDocument(documents: DocumentModel[]) {
    const mergedDocument = new Document();
    let contentType = '';

    // Merge documents.
    for (const document of documents) {
      const { encodedFileContent, mediaType } = document || {};

      contentType = mediaType;

      const buffer = await this.handleConvertBase64toBuffer({
        b64Data: encodedFileContent,
        contentType
      });

      const externalDocument = new ExternalDocument(buffer);

      mergedDocument.addPagesOf(externalDocument);
    }

    // Generate document.
    const documentBuffer = await mergedDocument.asBuffer();
    const blob = new Blob([documentBuffer], { type: contentType });

    // Open document.
    const url = window.URL.createObjectURL(blob);
    const win = window.open(url);

    // Check when the window is closed to remove the url for file after 10sec.
    // It stops when the window is closed.
    const interval = setInterval(() => {
      try {
        if (win.closed) {
          URL.revokeObjectURL(url);
          clearInterval(interval);
        }
      } catch {
        URL.revokeObjectURL(url);
        clearInterval(interval);
      }
    }, 10000);
  }

  /**
   * Handle convert base64 to buffer.
   */
  async handleConvertBase64toBuffer({
    b64Data = '',
    contentType = '',
    sliceSize = 512
  }: {
    b64Data: string;
    contentType: string;
    sliceSize?: number;
  }) {
    const byteCharacters = atob(b64Data);
    const byteArrays: Uint8Array[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });

    const buffer = await blob.arrayBuffer();

    return buffer;
  }
}
